import React, { memo, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import './index.scss';
import { defineCourseBuilderForm } from './CourseBuilderForm';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useDidMount } from '@core/utils/hooks/react';
import { R } from '@core/utils/r';
import { Storage } from '@core/utils/storage';
import { useBloc } from '@core/utils/bloc';
import { CourseBuilderPageBloc } from './CourseBuilderPageBloc';
import { CourseBuilderContext } from './CourseBuilderContext';
import { useLazyEffect } from '@core/utils/react';
import { useMainPageConfig, HeaderVisibility } from '@modules/dashboard/DashboardPage/MainPageContext';
import { CourseBuilderBlocs, CourseBuilderBlocsHandle } from './Components/CourseBuilderPage';
import { CourseBuilderSections } from './Components/SectionBar';
import { CourseBuilderHeader } from './Components/CourseBuilderHeader';
import { toMoment } from '@core/utils/time';
import { useRouteParam } from '@core/router';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { TopLoadingBar } from '@core/components/LoadingBar';
import { CourseBuilder } from '../Blocks/types';
import { GoogleFontLoader } from '@modules/common/GoogleFontLoader';
import { css } from '@emotion/react';
import { TextBlockVariation } from '../Blocks/TextBlock';

export const CourseBuilderPage = memo(() => {
  const lessonId = useRouteParam('id');
  const bloc = useBloc(CourseBuilderPageBloc, lessonId);
  const lesson = useObservable(bloc.lesson$);
  const loading = useLoadingState(bloc.loading);
  const form = useMemo(() => defineCourseBuilderForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const currentSection = useNonNilObservable(bloc.currentSection$);
  const [sidebar, setSidebar] = useState(true);
  const blockRefs = useMemo(() => R.times(formValue.blocks.length).map(() => React.createRef<HTMLDivElement>()), [formValue.blocks.length]);
  const [moveTo, setMoveto] = React.useState<number | null>(null);
  const [lastSaved, setLastSaved] = useState<number | null>(null);
  const [canSave, setCanSave] = useState(false);
  const [isInit, setInit] = useState(false);
  const blockHandlesRef = useRef<CourseBuilderBlocsHandle>(null);

  useEffect(() => {
    if (formValue) {
      setCanSave(true);
    }
  }, [formValue]);

  useLazyEffect(() => {
    if (moveTo != null && blockRefs[moveTo] && blockRefs[moveTo].current) {
      window.scrollTo({ top: blockRefs[moveTo].current!.offsetTop, behavior: 'smooth' });
      setMoveto(null);
    }
  }, [moveTo, blockRefs]);

  const toggleSidebar = useCallback(() => setSidebar(s => !s), []);

  useMainPageConfig(() => ({ footer: false, toolbar: false, header: HeaderVisibility.auto }));

  const onSave = useCallback(() => {
    if (loading.loaded) {
      setLastSaved(toMoment().unix());

      bloc.updateFormData(lessonId, JSON.stringify(formValue)).subscribe();
      setCanSave(false);
    }
  }, [bloc, lessonId, formValue, loading.loaded]);

  useLazyEffect(
    () => {
      if (formValue) onSave();
    },
    [formValue, onSave],
    5000,
  );

  useEffect(() => {
    if (loading.loaded && lesson && !isInit) {
      setInit(true);

      const data = lesson.data;
      if (data && data.blocks) {
        form.patchValue(data);
      } else {
        form.patchValue({
          title: lesson.name,
          blocks: [
            {
              id: 'section-title-0',
              type: CourseBuilder.BlockType.text,
              data: { title: 'Section Title' },
              variation: TextBlockVariation.paragraphWithHeading,
              menuOrder: 0,
              isSection: true,
              title: undefined,
            },
          ],
        });
      }
    }
  }, [loading, isInit, form, lesson]);

  if (loading.loading) return <TopLoadingBar />;

  return (
    <div id="course-builder" style={{ margin: 0 }}>
      <CourseBuilderContext.Provider value={{ form, currentSection, bloc, sidebar, toggleSidebar, lessonId }}>
        <CourseBuilderHeader id={lessonId} lastSaved={lastSaved} onSave={onSave} canSave={canSave} courseId={lesson?.courseId} />
        <CourseBuilderSections moveTo={setMoveto} handleRef={blockHandlesRef} />
        {!!formValue.font && <GoogleFontLoader font={formValue.font} />}
        <div
          css={css(`
          h1 {
            font-size: ${formValue.headingFontSize + 'px'};
          }
        `)}
          id="course-builder-container"
          style={{
            marginLeft: sidebar ? 250 : 0,
            fontSize: formValue.fontSize + 'px',
            fontFamily: formValue.font?.family,
            fontWeight: formValue.fontWeight,
          }}
        >
          <CourseBuilderBlocs handlesRef={blockHandlesRef} blockRefs={blockRefs} moveTo={setMoveto} lessonId={lessonId} />
        </div>
      </CourseBuilderContext.Provider>
    </div>
  );
});
